// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseCheckbox, BaseDynamicSelect, BaseInput, BaseSelect, BaseTable } from '~/components/common';
// Others
import { TypeErrorFormEnum } from '~/utils/enum/common';
import {
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  EMPTY_STRING,
  REG_EXP,
} from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getListVariant } from '~/thunks/variant/variantThunk';
import { getListInventoryProduct } from '~/thunks/inventory/inventoryThunk';
import { IQueryParamVariant, IVariant } from '~/utils/interface/variant';
import { formatInputCurrency, generateUniqueId } from '~/utils/helper/common';
import {
  IFormInventory,
  IInventoryProduct,
  IItemAddOn,
  IOptionAddOn,
  IParamInventoryProduct,
} from '~/utils/interface/inventory';
import { IBaseOption, IHeaderCellType, IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
// Styles, images, icons
import styles from './IncludeAddOnsInventoryForm.module.scss';
import { icons } from '~/assets';

type Props = {
  onRemoveAddOn?: (id: string) => void;
};

const cx = classNames.bind(styles);

const IncludeAddOnsInventoryForm = (props: Props) => {
  //#region Destructuring Props
  const { onRemoveAddOn } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/inventory', 'common/base']);
  const dispatch = useAppDispatch();

  const {
    control,
    setValue,
    setError,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<IFormInventory>();

  const { append: appendItems, remove: removeItems } = useFieldArray({
    control,
    name: 'addOns',
  });

  const { addOns: listAddOn } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleAddItem = () => {
    const newItem: IItemAddOn = {
      id: generateUniqueId(),
      addOnId: EMPTY_STRING,
      quantity: 0,
      price: EMPTY_STRING,
      isRequired: false,
      isShowOnWeb: false,
      variantIds: [],
    };

    appendItems(newItem);
    setError('addOns', { type: TypeErrorFormEnum.MANUAL, message: EMPTY_STRING });
  };

  const fetchOptionsListProductAddOn = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IOptionAddOn>> => {
      const { searchKey, page } = payload;
      const payloadParams: IParamInventoryProduct = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListInventoryProduct(payloadParams)).unwrap();

        const { responses: listInventory, pagination } = resp?.data;

        const formatOptions: IOptionAddOn[] = handleConvertOptionAddOn(listInventory);

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const fetchDataVariant = useCallback(async (payload: any): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, productId } = payload;
    const payloadParams: IQueryParamVariant = {
      page: page,
      limit: DEFAULT_LIMIT_PAGE,
      productId: productId,
    };

    try {
      const resp = await dispatch(getListVariant(payloadParams)).unwrap();

      const { responses: listVariant, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = handleConvertOptionVariant(listVariant);

      const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

      return { optionsResp: formatOptions, hasMoreResp: hasMore };
    } catch (error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const handleConvertOptionAddOn = (data: IInventoryProduct[]): IOptionAddOn[] => {
    const options: IOptionAddOn[] = data.map((item) => ({
      value: item.productId,
      label: item.name,
      price: item.rentalPrice,
      quantity: item.quantity,
      productId: item.productId,
      productValidate: item.id,
    }));

    return options;
  };

  const handleConvertOptionVariant = (data: IVariant[]): IBaseOption[] => {
    const options: IBaseOption[] = data.map((item) => ({
      value: item.id,
      label: item.name,
    }));

    return options;
  };

  const columnsItem = (t: TFunction): IHeaderCellType<IItemAddOn>[] => {
    return [
      {
        id: 'addOnId',
        label: t('inventory_form_add_on_title_name'),
        render: (record: IItemAddOn, index: number) => {
          return (
            <Controller
              key={`${record.id}-${index}-addOnId`}
              name={`addOns.${index}.addOnId`}
              control={control}
              render={({ field: { value } }) => (
                <BaseSelect<IOptionAddOn>
                  isSearch
                  value={value}
                  itemNameDefault={listAddOn?.[index]?.name}
                  callApiFn={fetchOptionsListProductAddOn}
                  type='loadMore'
                  onChange={(selected) => {
                    setValue(
                      `addOns.${index}`,
                      {
                        ...record,
                        addOnId: String(selected?.value),
                        price: String(selected?.price),
                        quantity: Number(selected?.quantity),
                        productId: String(selected?.productId),
                        productValidate: String(selected?.productValidate),
                        variantIds: [],
                      },
                      { shouldDirty: true, shouldValidate: true }
                    );
                    trigger('addOns');
                  }}
                  errorMessage={errors.addOns?.[index]?.addOnId?.message}
                />
              )}
            />
          );
        },
        minWidth: 250,
      },
      {
        id: 'variantIds',
        label: t('inventory_form_add_on_title_variants'),
        render: (record: IItemAddOn, index: number) => {
          return (
            <Controller
              key={`${record.id}-${index}-variantIds`}
              name={`addOns.${index}.variantIds`}
              control={control}
              render={({ field: { value, onChange } }) => {
                return (
                  <BaseDynamicSelect
                    productId={record.productId}
                    multiple={true}
                    callApiFn={fetchDataVariant}
                    value={value}
                    onChange={(selected) => {
                      onChange(selected.idSelected);
                    }}
                    errorMessage={errors.addOns?.[index]?.variantIds?.message}
                  />
                );
              }}
            />
          );
        },
        minWidth: 250,
      },
      {
        id: 'price',
        label: t('inventory_form_add_on_title_rental_price'),
        render: (record: IItemAddOn, index: number) => {
          return (
            <Controller
              key={`${record.id}-${index}-price`}
              name={`addOns.${index}.price`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput
                  borderRadius={'4px'}
                  value={value}
                  onChange={(event) => {
                    onChange(formatInputCurrency(event.target.value));
                  }}
                />
              )}
            />
          );
        },
        minWidth: 100,
      },
      {
        id: 'quantity',
        label: t('inventory_form_add_on_title_quantity'),
        align: 'center',
        render: (record: IItemAddOn, index: number) => {
          return (
            <Stack alignItems={'center'}>
              <Controller
                key={`${record.id}-${index}-quantity`}
                name={`addOns.${index}.quantity`}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Stack direction='row' spacing={1} alignItems='center' gap={2} className={cx('viewQuantity')}>
                    <IconButton
                      className={cx('buttonQty')}
                      onClick={() => {
                        const newQty = Math.max(Number(value) - 1, 0);
                        setValue(`addOns.${index}.quantity`, newQty);
                      }}
                    >
                      <img src={icons.commonIconMinus} alt={t('common_alt_img', 'common/base')} />
                    </IconButton>

                    <BaseInput
                      height={31}
                      textAlignCenter
                      borderRadius={'4px'}
                      value={String(value)}
                      onChange={(e) => {
                        const inputValue = e.target.value.replace(REG_EXP.NUMBER, EMPTY_STRING);
                        const numericValue = inputValue ? Number(inputValue) : EMPTY_STRING;
                        onChange(numericValue);
                      }}
                      errorMessage={errors.addOns?.[index]?.quantity?.message}
                    />

                    <IconButton
                      className={cx('buttonQty')}
                      onClick={() => {
                        const newQty = Number(value) + 1;
                        setValue(`addOns.${index}.quantity`, newQty);
                      }}
                    >
                      <img src={icons.commonIconPlusGrey} alt={t('common_alt_img', 'common/base')} />
                    </IconButton>
                  </Stack>
                )}
              />
            </Stack>
          );
        },
        minWidth: 100,
      },
      {
        id: 'isRequired',
        label: t('inventory_form_add_on_title_require'),
        render: (record: IItemAddOn, index: number) => {
          return (
            <Controller
              key={`${record.id}-${index}-isRequired`}
              name={`addOns.${index}.isRequired`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseCheckbox
                  checked={value}
                  label={t('inventory_form_add_on_title_require_checkbox')}
                  onChange={onChange}
                />
              )}
            />
          );
        },
        minWidth: 100,
      },
      {
        id: 'action',
        label: t('inventory_form_add_on_title_action'),
        minWidth: 100,
        align: 'center',
        render: (record: IItemAddOn, index: number) => {
          return (
            <IconButton
              className={cx('buttonIcon')}
              onClick={() => {
                removeItems(index);
                onRemoveAddOn?.(record.id || EMPTY_STRING);
              }}
            >
              <img
                className={cx('iconDelete')}
                src={icons.commonIconTrash}
                alt={t('common_alt_img', { ns: 'common/base' })}
              />
            </IconButton>
          );
        },
      },
    ];
  };
  //#endregion Handle Function

  return (
    <Stack id='addOnInventoryFormComponent' gap={4}>
      <Typography className={cx('header')}>{t('inventory_form_title_add_only')}</Typography>

      <BaseTable columns={columnsItem(t)} dataSource={listAddOn} />

      <Box>
        <Button
          type='button'
          variant='outlined'
          onClick={handleAddItem}
          startIcon={<img src={icons.commonIconPlusPrimary} alt={'common_alt_img'} />}
        >
          {t('inventory_form_add_on_title_add_item')}
        </Button>
      </Box>
    </Stack>
  );
};

export default IncludeAddOnsInventoryForm;
